import React, { useState, useEffect, useMemo } from "react";
import { Box, Typography, Tooltip, Select, MenuItem, Checkbox, ListItemText, Alert, FormControl, InputLabel, Button } from "@mui/material";
import CustomDropdown from '../../CustomDropdownWithPlus';
import styles from './index.module.css';
import { getTaxes } from '../../../services/tax';
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { computeAdditionalCostImpact } from "../../../utils/pricingEngine";
import {
  buildProductTaxPatch,
  buildPromoStoreTaxState,
  findTaxOptionById,
  normalizeRateDecimal,
  normalizeTaxIds,
  resolveTaxRateFromOption,
} from "../taxSelection";

// Booked/lock-aware summary right off formData
// helpers
const fromCents = (c) => (c || 0) / 100;

const normalizeTaxCode = (value) => String(value || '').trim().toUpperCase().replace(/[^A-Z0-9]/g, '');

const combinedGstProvinceCodes = new Set(['PBC', 'PMB', 'PQC', 'PSK']);

const getTaxCodeParts = (tax = {}) => {
  const type = normalizeTaxCode(tax?.type ?? tax?.Type ?? tax?.name ?? tax?.label);
  const stateCode = normalizeTaxCode(tax?.stateCode ?? tax?.StateCode ?? tax?.state ?? tax?.province);
  const name = normalizeTaxCode(tax?.name ?? tax?.label ?? tax?.title);
  const canonical = [type, stateCode, name].filter(Boolean).join('');
  return { type, stateCode, name, canonical };
};

const isCombinedGstProvinceTax = (tax = {}) => {
  const { type, stateCode, canonical } = getTaxCodeParts(tax);
  if (combinedGstProvinceCodes.has(stateCode)) return type === 'GST' || canonical.includes('GST');
  return type === 'GST' && /P(BC|MB|QC|SK)/.test(canonical);
};

const isStandaloneGstTax = (tax = {}) => {
  if (isCombinedGstProvinceTax(tax)) return false;
  const rate = normalizeRateDecimal(tax?.value ?? tax?.rate ?? tax?.Rate);
  const { type, stateCode, canonical } = getTaxCodeParts(tax);
  return type === 'GST' || stateCode === 'GST' || (canonical.includes('GST') && Math.abs(rate - 0.05) < 0.0001);
};

const normalizeSelectedTaxIdsForCombinedTaxes = (ids = [], taxOptions = []) => {
  const byId = new Map((taxOptions || []).map((tax) => [String(tax?.id ?? tax?.taxId), tax]));
  const combinedTax = ids.map((id) => byId.get(String(id))).find((tax) => tax && isCombinedGstProvinceTax(tax));
  if (!combinedTax) return { taxIds: ids, warning: '' };

  const filtered = ids.filter((id) => !isStandaloneGstTax(byId.get(String(id)) || {}));
  const removedGst = filtered.length !== ids.length;
  const combinedLabel = combinedTax?.stateCode ? `GST/${String(combinedTax.stateCode).toUpperCase()}` : (combinedTax?.name || 'the selected combined tax');
  return {
    taxIds: filtered,
    warning: removedGst
      ? `GST is already included in ${combinedLabel}. We removed standalone GST to avoid double-counting.`
      : '',
  };
};

const resolveSelectedTaxes = (formData, taxOptions = []) => {
  const normalizedIds = normalizeTaxIds(formData);
  if (normalizedIds.length) {
    const byId = new Map((taxOptions || []).map((t) => [String(t?.id), t]));
    const selected = normalizedIds.map((id) => {
      const option = byId.get(String(id));
      if (!option) return null;
      const rate = normalizeRateDecimal(option?.value ?? option?.rate ?? option?.Rate);
      if (rate <= 0) return null;
      return {
        id,
        label: option?.name || option?.label || option?.type || `Tax ${id}`,
        rate,
      };
    }).filter(Boolean);
    if (selected.length) return selected;
  }

  const snapshotRate = normalizeRateDecimal(formData?.selectedTaxRate);
  const optionRate = normalizeRateDecimal((taxOptions || []).find((t) => String(t.id) === String(formData?.selectedTax))?.value);
  const fallbackRate = snapshotRate || optionRate;
  const fallbackId = Number(formData?.selectedTax ?? formData?.taxId);
  if (fallbackRate > 0) {
    return [{
      id: Number.isFinite(fallbackId) && fallbackId > 0 ? fallbackId : null,
      label: Number.isFinite(fallbackId) && fallbackId > 0 ? `Tax ${fallbackId}` : 'Tax',
      rate: fallbackRate,
    }];
  }

  return [];
};

const computeTaxBreakdown = (subtotal, selectedTaxes = []) => {
  const safeSubtotal = Number(subtotal) || 0;
  const breakdown = (Array.isArray(selectedTaxes) ? selectedTaxes : []).map((tax) => ({
    ...tax,
    taxAmount: +((safeSubtotal * (Number(tax?.rate) || 0)).toFixed(2)),
  }));
  const taxAmount = breakdown.reduce((sum, row) => sum + (Number(row?.taxAmount) || 0), 0);
  return { breakdown, taxAmount: +taxAmount.toFixed(2) };
};


const CURRENCY_FLAGS = {
  CAD: "https://flagcdn.com/w20/ca.png",
  USD: "https://flagcdn.com/w20/us.png",
};

const normalizeCurrencyCode = (value) => String(value || "").trim().toUpperCase();

const CurrencyOption = ({ code }) => {
  const normalized = normalizeCurrencyCode(code);
  const flag = CURRENCY_FLAGS[normalized];
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {flag && <img src={flag} alt="" width={20} height={14} style={{ objectFit: "cover", borderRadius: 2 }} />}
      <span>{normalized}</span>
    </Box>
  );
};


const roundMoney = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? Number(n.toFixed(2)) : 0;
};

const roundRate = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? Number(n.toFixed(6)) : null;
};

const resolveExchangeRate = (currencySettings = {}, sourceCurrency, pricingCurrency) => {
  const source = normalizeCurrencyCode(sourceCurrency);
  const target = normalizeCurrencyCode(pricingCurrency);
  if (!source || !target || source === target) return null;

  const rates = currencySettings?.exchangeRates || {};
  const direct = Number(rates[`${source}:${target}`]);
  if (Number.isFinite(direct) && direct > 0) return roundRate(direct);

  const reverse = Number(rates[`${target}:${source}`]);
  if (Number.isFinite(reverse) && reverse > 0) return roundRate(1 / reverse);

  return null;
};


const rowHasConvertedCostBasis = (row = {}) => {
  const converted = Number(row.convertedNetCost ?? row.ConvertedNetCost);
  const rate = Number(row.exchangeRate ?? row.ExchangeRate);
  return Number.isFinite(converted) && converted > 0 && Number.isFinite(rate) && rate > 0;
};

const rowHasConvertedCostValue = (row = {}) => {
  const converted = Number(row.convertedNetCost ?? row.ConvertedNetCost);
  return Number.isFinite(converted) && converted > 0;
};

const calculateMarginFromSourceCost = (row = {}) => {
  const sourceCost = Number(row.netCost ?? row.NetCost ?? 0) || 0;
  const sellPrice = Number(row.clientPrice ?? row.ClientPrice ?? row.retail ?? row.Retail ?? 0) || 0;
  if (!(sellPrice > 0)) return null;
  return Number((((sellPrice - sourceCost) / sellPrice) * 100).toFixed(2));
};

const withEffectiveCostBasis = (row = {}) => {
  if (!rowHasConvertedCostBasis(row)) return row;
  const effectiveCost = Number(row.convertedNetCost ?? row.ConvertedNetCost);
  return {
    ...row,
    netCost: effectiveCost,
    NetCost: effectiveCost,
  };
};

const clearConvertedRow = (row = {}) => {
  const shouldRecalculateMargin = rowHasConvertedCostValue(row);
  const recalculatedMargin = shouldRecalculateMargin
    ? calculateMarginFromSourceCost(row)
    : null;

  return {
    ...row,
    convertedNetCost: null,
    ConvertedNetCost: null,
    exchangeRate: null,
    ExchangeRate: null,
    convertedCurrency: null,
    ...(recalculatedMargin != null
      ? { margin: recalculatedMargin, Margin: recalculatedMargin }
      : {}),
  };
};

const convertPricingRow = (row = {}, exchangeRate) => {
  const netCost = Number(row.netCost ?? row.NetCost ?? 0) || 0;
  const existingMargin = Number(row.margin ?? row.Margin ?? 0) || 0;
  const convertedNetCost = roundMoney(netCost * exchangeRate);
  const exchangeRateRounded = roundRate(exchangeRate);
  const sellPrice = Number(row.clientPrice ?? row.ClientPrice ?? row.retail ?? row.Retail ?? 0) || 0;
  const nextMargin = sellPrice > 0
    ? Number((((sellPrice - convertedNetCost) / sellPrice) * 100).toFixed(2))
    : existingMargin;

  return {
    ...row,
    convertedNetCost,
    ConvertedNetCost: convertedNetCost,
    exchangeRate: exchangeRateRounded,
    ExchangeRate: exchangeRateRounded,
    margin: nextMargin,
    Margin: nextMargin,
  };
};
const getDecorationRowCurrencies = (row = {}, fallbackPricingCurrency = "") => {
  const sourceCurrency = normalizeCurrencyCode(row.CurrencyCode || row.currencyCode);
  const pricingCurrency = normalizeCurrencyCode(
    row.PricingCurrencyCode || row.pricingCurrencyCode || fallbackPricingCurrency
  );
  return { sourceCurrency, pricingCurrency };
};

const getDecorationRows = (decorations = []) => (
  (Array.isArray(decorations) ? decorations : [])
    .flatMap((decoration) => (Array.isArray(decoration?.rows) ? decoration.rows : []))
);

const getMissingDecorationExchangePairs = (decorations = [], fallbackPricingCurrency = "", currencySettings = {}) => {
  const missing = new Set();
  getDecorationRows(decorations).forEach((row) => {
    const { sourceCurrency, pricingCurrency } = getDecorationRowCurrencies(row, fallbackPricingCurrency);
    if (!sourceCurrency || !pricingCurrency || sourceCurrency === pricingCurrency) return;
    const rate = resolveExchangeRate(currencySettings, sourceCurrency, pricingCurrency);
    if (!(rate > 0)) missing.add(`${sourceCurrency}:${pricingCurrency}`);
  });
  return Array.from(missing);
};

const convertDecorationRow = (row = {}, fallbackPricingCurrency = "", currencySettings = {}) => {
  const { sourceCurrency, pricingCurrency } = getDecorationRowCurrencies(row, fallbackPricingCurrency);

  if (!sourceCurrency || !pricingCurrency || sourceCurrency === pricingCurrency) {
    return clearConvertedRow(row);
  }

  const rowExchangeRate = resolveExchangeRate(currencySettings, sourceCurrency, pricingCurrency);
  if (!(rowExchangeRate > 0)) return row;

  const netCost = Number(row.netCost ?? row.NetCost ?? 0) || 0;
  const convertedNetCost = roundMoney(netCost * rowExchangeRate);
  const exchangeRateRounded = roundRate(rowExchangeRate);
  const existingMargin = Number(row.margin ?? row.Margin ?? 0) || 0;
  const sellPrice = Number(row.clientPrice ?? row.ClientPrice ?? row.retail ?? row.Retail ?? 0) || 0;
  const nextMargin = sellPrice > 0
    ? Number((((sellPrice - convertedNetCost) / sellPrice) * 100).toFixed(2))
    : existingMargin;

  return {
    ...row,
    ConvertedNetCost: convertedNetCost,
    convertedNetCost: convertedNetCost,
    ExchangeRate: exchangeRateRounded,
    exchangeRate: exchangeRateRounded,
    convertedCurrency: pricingCurrency,
    Margin: nextMargin,
    margin: nextMargin,
    PricingCurrencyCode: pricingCurrency,
    pricingCurrencyCode: pricingCurrency,
  };
};

const mapDecorationRows = (decorations = [], mapper) => (
  (Array.isArray(decorations) ? decorations : []).map((decoration) => ({
    ...decoration,
    rows: Array.isArray(decoration?.rows) ? decoration.rows.map(mapper) : decoration?.rows,
  }))
);


const computeBookedSummaryFromForm = (formData, taxOptions = []) => {
  const rows = Array.isArray(formData?.pricingRows)
    ? formData.pricingRows.map(withEffectiveCostBasis)
    : [];
  const decos = Array.isArray(formData?.decorations) ? formData.decorations : [];

  const additionalRows = decos.flatMap((d) => (Array.isArray(d?.rows) ? d.rows : []));
  const {
    clientPriceCents: revenueAllC,
    marginCents: marginC,
  } = computeAdditionalCostImpact({
    baseRows: rows,
    additionalRows,
  });

  const subtotalAll = fromCents(revenueAllC);
  const selectedTaxes = resolveSelectedTaxes(formData, taxOptions);
  const { breakdown: taxBreakdown, taxAmount } = computeTaxBreakdown(subtotalAll, selectedTaxes);
  const total = +(subtotalAll + taxAmount).toFixed(2);
  const marginAmount = fromCents(marginC);

  // Margin % is derived from subtotal and computed margin dollars:
  // (Subtotal - Costs) / Subtotal
  const marginPercentage = subtotalAll > 0
    ? +(((marginAmount) / subtotalAll) * 100).toFixed(2)
    : 0;

  return { subtotal: subtotalAll, marginAmount, marginPercentage, taxAmount, total, taxBreakdown };
};


const PricingSummary = ({
  setFormData,
  subtotal,
  marginPercentage,
  formData,
  marginAmount,
  taxRate,
  taxAmount,
  artworkAmount,
  total,
  decorationCharges,
  currencySettings
}) => {
  const [taxOptions, setTaxOptions] = useState([]);
  const [showMultiTax, setShowMultiTax] = useState(false);
  const [taxSelectionWarning, setTaxSelectionWarning] = useState('');
  const [conversionWarning, setConversionWarning] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const taxesData = await getTaxes();
        setTaxOptions(taxesData?.taxes || []);
      } catch (e) { /* ignore */ }
    })();
  }, []);

  const booked = useMemo(() => computeBookedSummaryFromForm(formData, taxOptions), [formData, taxOptions]);

  const displaySubtotal = Number.isFinite(booked?.subtotal) ? booked.subtotal : (subtotal ?? 0);
  const displayMarginAmt = Number.isFinite(booked?.marginAmount) ? booked.marginAmount : (marginAmount ?? 0);
  const displayMarginPct = Number.isFinite(booked?.marginPercentage) ? booked.marginPercentage : (marginPercentage ?? 0);
  const displayTaxAmount = Number.isFinite(booked?.taxAmount) ? booked.taxAmount : (taxAmount ?? 0);
  const displayTotal = Number.isFinite(booked?.total) ? booked.total : (total ?? 0);

  const handleSelect = (e, name) => {
    const value = e.target.value;
    if (name !== 'selectedTax') {
      setFormData((prev) => ({ ...prev, [name]: value }));
      return;
    }

    const selectedTaxId = Number(value);
    let nextWarning = '';
    setFormData((prev) => {
      const candidateTaxIds = Number.isFinite(selectedTaxId) && selectedTaxId > 0
        ? [selectedTaxId]
        : [];
      const { taxIds: nextTaxIds, warning } = normalizeSelectedTaxIdsForCombinedTaxes(candidateTaxIds, taxOptions);
      const firstTaxId = nextTaxIds.length ? nextTaxIds[0] : null;
      const selectedTaxOption = findTaxOptionById(taxOptions, firstTaxId);
      nextWarning = warning;

      return {
        ...prev,
        ...buildProductTaxPatch(selectedTaxOption),
        selectedTaxRate: firstTaxId == null
          ? null
          : resolveTaxRateFromOption(taxOptions, firstTaxId),
      };
    });
    setTaxSelectionWarning(nextWarning);
  };

  const selectedTaxIds = useMemo(() => normalizeTaxIds(formData), [formData]);

  useEffect(() => {
    if (!taxOptions.length || !selectedTaxIds.length) return;
    const { taxIds: normalizedTaxIds, warning } = normalizeSelectedTaxIdsForCombinedTaxes(selectedTaxIds, taxOptions);
    const changed = normalizedTaxIds.length !== selectedTaxIds.length
      || normalizedTaxIds.some((id, index) => id !== selectedTaxIds[index]);
    if (!changed) return;

    const firstTaxId = normalizedTaxIds.length ? normalizedTaxIds[0] : null;
    setTaxSelectionWarning(warning);
    setFormData((prev) => ({
      ...prev,
      ...buildPromoStoreTaxState(
        {
          ...prev,
          taxIds: normalizedTaxIds,
          taxId: firstTaxId,
          selectedTax: firstTaxId != null ? String(firstTaxId) : '',
        },
        taxOptions
      ),
    }));
  }, [selectedTaxIds, setFormData, taxOptions]);

  const handleMultiTaxChange = (e) => {
    const raw = e.target.value;
    const arr = Array.isArray(raw) ? raw : [raw];
    const seen = new Set();
    const nextTaxIds = arr
      .map(Number)
      .filter((id) => Number.isFinite(id) && id > 0)
      .filter((id) => {
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      });

    const { taxIds: normalizedTaxIds, warning } = normalizeSelectedTaxIdsForCombinedTaxes(nextTaxIds, taxOptions);
    const firstTaxId = normalizedTaxIds.length ? normalizedTaxIds[0] : null;
    setTaxSelectionWarning(warning);
    setFormData((prev) => ({
      ...prev,
      ...buildPromoStoreTaxState(
        {
          ...prev,
          taxIds: normalizedTaxIds,
          taxId: firstTaxId,
          selectedTax: firstTaxId != null ? String(firstTaxId) : '',
        },
        taxOptions
      ),
    }));
  };

  const taxBreakdown = Array.isArray(booked?.taxBreakdown) ? booked.taxBreakdown : [];
  const enabledPricingCurrencies = useMemo(() => {
    const raw = Array.isArray(currencySettings?.enabledCurrencyCodes)
      ? currencySettings.enabledCurrencyCodes
      : [];
    const normalized = raw.map(normalizeCurrencyCode).filter(Boolean);
    const fallback = normalizeCurrencyCode(
      currencySettings?.defaultCurrencyCode ||
      formData?.PricingCurrencyCode ||
      formData?.CurrencyCode ||
      "CAD"
    );
    return Array.from(new Set([fallback, ...normalized]));
  }, [currencySettings, formData?.CurrencyCode, formData?.PricingCurrencyCode]);
  const pricingCurrencyCode = normalizeCurrencyCode(
    formData?.PricingCurrencyCode ||
    currencySettings?.defaultCurrencyCode ||
    formData?.CurrencyCode ||
    "CAD"
  );
  const sourceCurrencyCode = normalizeCurrencyCode(formData?.CurrencyCode);
  const showProductConversion = Boolean(sourceCurrencyCode && pricingCurrencyCode && sourceCurrencyCode !== pricingCurrencyCode);
  const exchangeRate = resolveExchangeRate(currencySettings, sourceCurrencyCode, pricingCurrencyCode);
  const pricingRows = Array.isArray(formData?.pricingRows) ? formData.pricingRows : [];
  const decorationRows = getDecorationRows(formData?.decorations);
  const hasConvertibleDecorationRows = decorationRows.some((row) => {
    const { sourceCurrency, pricingCurrency } = getDecorationRowCurrencies(row, pricingCurrencyCode);
    return Boolean(sourceCurrency && pricingCurrency && sourceCurrency !== pricingCurrency);
  });
  const missingDecorationExchangePairs = getMissingDecorationExchangePairs(formData?.decorations, pricingCurrencyCode, currencySettings);
  const firstConvertedPricingRow = pricingRows.find(rowHasConvertedCostBasis) || null;
  const firstConvertedDecorationRow = decorationRows.find(rowHasConvertedCostBasis) || null;
  const firstDecorationExchangeRate = decorationRows.reduce((found, row) => {
    if (found) return found;
    const { sourceCurrency, pricingCurrency } = getDecorationRowCurrencies(row, pricingCurrencyCode);
    return resolveExchangeRate(currencySettings, sourceCurrency, pricingCurrency);
  }, null);
  const hasConvertedRows = Boolean(firstConvertedPricingRow || firstConvertedDecorationRow);
  const activeConversionRate = firstConvertedPricingRow
    ? roundRate(firstConvertedPricingRow.exchangeRate ?? firstConvertedPricingRow.ExchangeRate)
    : (firstConvertedDecorationRow ? roundRate(firstConvertedDecorationRow.exchangeRate ?? firstConvertedDecorationRow.ExchangeRate) : null);
  const displayedConversionRate = activeConversionRate ?? exchangeRate ?? firstDecorationExchangeRate;
  const displayedRateIsProductPair = Boolean(showProductConversion && exchangeRate);
  const conversionSourceLabel = showProductConversion
    ? sourceCurrencyCode
    : (hasConvertibleDecorationRows ? "Mixed decoration sources" : (sourceCurrencyCode || "—"));
  const missingExchangeLabel = showProductConversion
    ? `${sourceCurrencyCode} → ${pricingCurrencyCode}`
    : missingDecorationExchangePairs.join(", ");
  const showConversionPanel = showProductConversion || hasConvertibleDecorationRows || hasConvertedRows;
  const hasProductExchangeRate = !showProductConversion || exchangeRate > 0;

  const handleConvertPricingRows = () => {
    const missingPairs = getMissingDecorationExchangePairs(formData?.decorations, pricingCurrencyCode, currencySettings);
    const canConvertProductRows = showProductConversion && exchangeRate > 0;
    const canConvertAnyDecorationRows = hasConvertibleDecorationRows && missingPairs.length < decorationRows.filter((row) => {
      const { sourceCurrency, pricingCurrency } = getDecorationRowCurrencies(row, pricingCurrencyCode);
      return Boolean(sourceCurrency && pricingCurrency && sourceCurrency !== pricingCurrency);
    }).length;

    if (!canConvertProductRows && !canConvertAnyDecorationRows) {
      setConversionWarning(missingPairs.length
        ? `Missing exchange rate${missingPairs.length > 1 ? "s" : ""}: ${missingPairs.join(", ")}.`
        : "No conversion needed.");
      return;
    }

    setConversionWarning(missingPairs.length
      ? `Skipped decoration row${missingPairs.length > 1 ? "s" : ""} with missing exchange rate${missingPairs.length > 1 ? "s" : ""}: ${missingPairs.join(", ")}.`
      : "");

    setFormData((prev) => ({
      ...prev,
      PricingCurrencyCode: pricingCurrencyCode,
      pricingRows: (Array.isArray(prev?.pricingRows) ? prev.pricingRows : []).map((row) => (
        canConvertProductRows ? convertPricingRow(row, exchangeRate) : row
      )),
      decorations: mapDecorationRows(prev?.decorations, (row) => (
        convertDecorationRow(row, pricingCurrencyCode, currencySettings)
      )),
    }));
  };

  const handleClearConversion = () => {
    setConversionWarning("");
    setFormData((prev) => ({
      ...prev,
      pricingRows: (Array.isArray(prev?.pricingRows) ? prev.pricingRows : []).map(clearConvertedRow),
      decorations: mapDecorationRows(prev?.decorations, clearConvertedRow),
    }));
  };

  return (
    <Box className={`${styles.pricingSummaryContainer} edit-product-popup-pricing-summary`}>
      <Box className={styles.marginBox}>
        <Box className={styles.flexRow}>
          <Tooltip
            arrow
            placement="top"
            componentsProps={{ tooltip: { sx: { maxWidth: 360, fontSize: '0.85rem' } } }}
            title={
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  How we calculate booked margin
                </Typography>
                <div><b>Booked formula:</b> Margin = Subtotal − Costs</div>
                <div style={{ marginTop: 6 }}>
                  <b>Booked Margin %:</b> (Subtotal − Costs) ÷ Subtotal × 100
                </div>
                <div style={{ marginTop: 6 }}>
                  <b>Lock:</b> When a line is locked, we trust its Retail and do not recompute it from cost — only Net&nbsp;Cost may change (for POs).
                </div>
                <div style={{ marginTop: 6 }}>
                  <b>Note:</b> Tax is added after pricing and does <u>not</u> affect margin %.
                </div>
              </Box>
            }
          >
            <Typography sx={{ cursor: "help", display: "inline-flex", alignItems: "center", gap: 0.5 }}>
              Booked Margin Percentage <InfoOutlinedIcon fontSize="small" />
            </Typography>
          </Tooltip>

          <Typography fontWeight="bold">
            {displayMarginPct.toFixed(2)}%
          </Typography>
        </Box>

        <Box className={styles.flexRow}>
          <Typography>Booked Margin Amount</Typography>
          <Typography fontWeight="bold">${displayMarginAmt.toFixed(2)}</Typography>
        </Box>

        <Box sx={{ mt: 1.5 }}>
          <FormControl size="small" fullWidth>
            <InputLabel id="pricing-currency-label">Pricing Currency</InputLabel>
            <Select
              labelId="pricing-currency-label"
              label="Pricing Currency"
              value={pricingCurrencyCode}
              disabled={hasConvertedRows}
              onChange={(e) => setFormData((prev) => ({ ...prev, PricingCurrencyCode: normalizeCurrencyCode(e.target.value) }))}
              renderValue={(value) => <CurrencyOption code={value} />}
            >
              {enabledPricingCurrencies.map((code) => (
                <MenuItem key={code} value={code}>
                  <CurrencyOption code={code} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {showConversionPanel && (
            <Box sx={{ mt: 1, p: 1.25, border: "1px solid #e2e8f0", borderRadius: 1, bgcolor: "#f8fafc" }}>
              <Typography variant="caption" sx={{ display: "block", fontWeight: 700 }}>
                Source Currency: {conversionSourceLabel}
              </Typography>
              <Typography variant="caption" sx={{ display: "block", fontWeight: 700 }}>
                Pricing Currency: {pricingCurrencyCode}
              </Typography>
              {(displayedConversionRate || hasConvertedRows) ? (
                <>
                  {displayedConversionRate && (
                    <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
                      Exchange Rate: {displayedRateIsProductPair ? `1 ${sourceCurrencyCode} = ${displayedConversionRate} ${pricingCurrencyCode}` : `available for ${pricingCurrencyCode}` }
                    </Typography>
                  )}
                  {hasConvertedRows ? (
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{ mt: 1, textTransform: "none" }}
                      onClick={handleClearConversion}
                    >
                      Clear Conversion
                    </Button>
                  ) : (
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{ mt: 1, textTransform: "none" }}
                      onClick={handleConvertPricingRows}
                    >
                      Convert costs to {pricingCurrencyCode}
                    </Button>
                  )}
                  {hasConvertedRows && (
                    <Typography variant="caption" sx={{ display: "block", mt: 0.75, color: "text.secondary" }}>
                      Converts product and decoration source costs into {pricingCurrencyCode} for margin cost basis. Customer pricing is unchanged.
                    </Typography>
                  )}
                </>
              ) : (
                <Alert severity="warning" sx={{ mt: 1, fontSize: "0.78rem", py: 0.5 }}>
                  No exchange rate configured for {missingExchangeLabel}. Add one in Company Settings &gt; Currency.
                </Alert>
              )}
              {(conversionWarning || (missingDecorationExchangePairs.length > 0 && hasProductExchangeRate)) && (
                <Alert severity="warning" sx={{ mt: 1, fontSize: "0.78rem", py: 0.5 }}>
                  {conversionWarning || `Missing decoration exchange rate${missingDecorationExchangePairs.length > 1 ? "s" : ""}: ${missingDecorationExchangePairs.join(", ")}.`}
                </Alert>
              )}
            </Box>
          )}
        </Box>
      </Box>

      <Box className={styles.pricingBox}>
        <Box className={styles.flexRow}>
          <Typography>Subtotal:</Typography>
          <Typography fontWeight="bold">${displaySubtotal.toFixed(2)}</Typography>
        </Box>

        <Box className={styles.flexRow} mt={1}>
          <Box className="field">
            <CustomDropdown
              isRequired={true}
              label="Default Tax"
              selectedValue={formData?.selectedTax ?? ''}
              name="selectedTax"
              options={taxOptions}
              handleSelect={handleSelect}
            />
            <Typography
              sx={{ mt: 0.75, fontSize: '0.85rem', color: 'primary.main', cursor: 'pointer', textDecoration: 'underline' }}
              onClick={() => setShowMultiTax((prev) => !prev)}
            >
              Add more options
            </Typography>

            {taxSelectionWarning && (
              <Alert severity="warning" sx={{ mt: 1, fontSize: '0.8rem', alignItems: 'center' }}>
                {taxSelectionWarning}
              </Alert>
            )}

            {showMultiTax && (
              <Select
                multiple
                size="small"
                fullWidth
                value={selectedTaxIds}
                onChange={handleMultiTaxChange}
                renderValue={(selected) => {
                  const ids = Array.isArray(selected) ? selected.map(String) : [];
                  const labels = taxOptions
                    .filter((opt) => ids.includes(String(opt.id)))
                    .map((opt) => opt.name);
                  return labels.join(', ');
                }}
                sx={{ mt: 1 }}
              >
                {(taxOptions || []).map((tax) => {
                  const checked = selectedTaxIds.some((id) => String(id) === String(tax.id));
                  const combinedSelected = selectedTaxIds.some((id) => {
                    const selectedTax = taxOptions.find((opt) => String(opt.id) === String(id));
                    return selectedTax && isCombinedGstProvinceTax(selectedTax);
                  });
                  const disabled = combinedSelected && isStandaloneGstTax(tax);
                  return (
                    <MenuItem key={tax.id} value={Number(tax.id)} disabled={disabled}>
                      <Checkbox checked={checked} disabled={disabled} />
                      <ListItemText primary={tax.name} secondary={disabled ? 'Included in selected GST/PST tax' : null} />
                    </MenuItem>
                  );
                })}
              </Select>
            )}
            {taxBreakdown.length > 1 && (
              <Box sx={{ mt: 1 }}>
                {taxBreakdown.map((row) => (
                  <Typography key={`${row.id}-${row.label}`} variant="caption" display="block">
                    {row.label} ({(row.rate * 100).toFixed(2).replace(/\.00$/, '')}%): ${Number(row.taxAmount || 0).toFixed(2)}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
          <Tooltip
            arrow
            placement="top"
            title="Tax is applied after pricing and does not change the booked margin %."
          >
            <Typography fontWeight="bold">
              ${displayTaxAmount.toFixed(2)}
            </Typography>
          </Tooltip>
        </Box>

        <Box className={styles.flexRow} mt={2}>
          <Typography variant="h6" fontWeight="bold">Total:</Typography>
          <Typography variant="h6" fontWeight="bold">${displayTotal.toFixed(2)}</Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default PricingSummary;
